/**
 * Centralized API service layer.
 * Wraps axios calls with consistent error handling and request patterns.
 * Components should import from here instead of calling axios directly.
 */
import axios from '../config/axios';
import { API_ENDPOINTS } from '../config/api';

// ─── Fields ──────────────────────────────────────────────────────────
export const fieldsApi = {
  getAll: () => axios.get(API_ENDPOINTS.FIELDS),
  getAllWithData: () => axios.get(API_ENDPOINTS.FIELDS_ALL_WITH_DATA),
  getDetail: (id) => axios.get(API_ENDPOINTS.FIELD_DETAIL(id)),
  getViewData: (id) => axios.get(API_ENDPOINTS.FIELD_VIEW_DATA(id)),
  create: (data) => axios.post(API_ENDPOINTS.FIELDS, data),
  update: (id, data) => axios.patch(API_ENDPOINTS.FIELD_DETAIL(id), data),
  delete: (id) => axios.delete(API_ENDPOINTS.FIELD_DETAIL(id)),
  restore: (id) => axios.post(API_ENDPOINTS.FIELD_RESTORE(id)),
  origin: (id) => axios.get(API_ENDPOINTS.FIELD_ORIGIN(id)),
  recomputeSoilType: (id) => axios.post(API_ENDPOINTS.FIELD_RECOMPUTE_SOIL_TYPE(id)),
  // Seasons
  getSeasons:    (fieldId)           => axios.get(API_ENDPOINTS.FIELD_SEASONS(fieldId)),
  createSeason:  (fieldId, data)     => axios.post(API_ENDPOINTS.FIELD_SEASONS(fieldId), data),
  updateSeason:  (fieldId, id, data) => axios.patch(API_ENDPOINTS.FIELD_SEASON_DETAIL(fieldId, id), data),
  deleteSeason:  (fieldId, id)       => axios.delete(API_ENDPOINTS.FIELD_SEASON_DETAIL(fieldId, id)),
};

// ─── Zones ───────────────────────────────────────────────────────────
export const zonesApi = {
  getByField: (fieldId) => axios.get(API_ENDPOINTS.FIELD_ZONES(fieldId)),
  create: (fieldId, data) => axios.post(API_ENDPOINTS.FIELD_ZONES(fieldId), data),
  update: (fieldId, zoneId, data) =>
    axios.put(API_ENDPOINTS.FIELD_ZONE_DETAIL(fieldId, zoneId), data),
  delete: (fieldId, zoneId) =>
    axios.delete(API_ENDPOINTS.FIELD_ZONE_DETAIL(fieldId, zoneId)),
  setControlMode: (zoneId, mode) =>
    axios.patch(API_ENDPOINTS.ZONE_CONTROL_MODE(zoneId), { control_mode: mode }),
};

// ─── Irrigation ──────────────────────────────────────────────────────
export const irrigationApi = {
  getTasks: (params) => axios.get(API_ENDPOINTS.IRRIGATION_TASKS, { params }),
  getToday: () => axios.get(API_ENDPOINTS.IRRIGATION_TODAY),
  getZones: () => axios.get(API_ENDPOINTS.IRRIGATION_ZONES),
  cancelTask: (id) =>
    axios.post(API_ENDPOINTS.IRRIGATION_TASK_ACTION(id, 'cancel')),
  startTask: (id) =>
    axios.post(API_ENDPOINTS.IRRIGATION_TASK_ACTION(id, 'start')),
  stopTask: (id) =>
    axios.post(API_ENDPOINTS.IRRIGATION_TASK_ACTION(id, 'stop')),
  manualStartZone: (zoneId, data) =>
    axios.post(API_ENDPOINTS.MANUAL_START_ZONE(zoneId), data),
  manualStopZone: (zoneId) =>
    axios.post(API_ENDPOINTS.MANUAL_STOP_ZONE(zoneId)),
  emergencyStop: () => axios.post(API_ENDPOINTS.EMERGENCY_STOP),
  getPlans: (params) => axios.get(API_ENDPOINTS.IRRIGATION_PLANS, { params }),
  getByZone: (params) => axios.get(API_ENDPOINTS.IRRIGATION_BY_ZONE, { params }),
};

// ─── Weekly Schedules ────────────────────────────────────────────────
export const schedulesApi = {
  getAll: (params) => axios.get(API_ENDPOINTS.WEEKLY_SCHEDULES, { params }),
  getDetail: (id) => axios.get(API_ENDPOINTS.WEEKLY_SCHEDULE_DETAIL(id)),
  create: (data) => axios.post(API_ENDPOINTS.WEEKLY_SCHEDULES, data),
  update: (id, data) => axios.put(API_ENDPOINTS.WEEKLY_SCHEDULE_DETAIL(id), data),
  patch: (id, data) => axios.patch(API_ENDPOINTS.WEEKLY_SCHEDULE_DETAIL(id), data),
  delete: (id) => axios.delete(API_ENDPOINTS.WEEKLY_SCHEDULE_DETAIL(id)),
};

// ─── Alerts ──────────────────────────────────────────────────────────
export const alertsApi = {
  getActive: () => axios.get(API_ENDPOINTS.ALERTS_ACTIVE),
  getAll: (params) => axios.get(API_ENDPOINTS.ALERTS_LIST, { params }),
  getDetail: (id) => axios.get(API_ENDPOINTS.ALERT_DETAIL(id)),
  acknowledge: (id) => axios.post(API_ENDPOINTS.ALERT_ACKNOWLEDGE(id)),
  update: (id, data) => axios.patch(API_ENDPOINTS.ALERT_DETAIL(id), data),
  requestHelp: (data) => axios.post(API_ENDPOINTS.ALERT_REQUEST_HELP, data),
};

// ─── Sensors ─────────────────────────────────────────────────────────
export const sensorsApi = {
  getDevices: () => axios.get(API_ENDPOINTS.SENSORS_DEVICES),
  getDeviceDetail: (id) => axios.get(API_ENDPOINTS.SENSOR_DEVICE_DETAIL(id)),
  createDevice: (data) => axios.post(API_ENDPOINTS.SENSORS_DEVICES, data),
  updateDevice: (id, data) =>
    axios.put(API_ENDPOINTS.SENSOR_DEVICE_DETAIL(id), data),
  deleteDevice: (id) => axios.delete(API_ENDPOINTS.SENSOR_DEVICE_DETAIL(id)),
  getReadings: (params) => axios.get(API_ENDPOINTS.SENSORS_READINGS, { params }),
  getTimeseries: (params) =>
    axios.get(API_ENDPOINTS.SENSORS_TIMESERIES, { params }),
  getStatistics: (params) =>
    axios.get(API_ENDPOINTS.SENSORS_STATISTICS, { params }),
  getProfile: (params) =>
    axios.get(API_ENDPOINTS.SENSORS_PROFILE, { params }),
};

// ─── Weather ─────────────────────────────────────────────────────────
export const weatherApi = {
  getCurrent: () => axios.get(API_ENDPOINTS.WEATHER_CURRENT),
  getForecast: () => axios.get(API_ENDPOINTS.WEATHER_FORECAST),
  getHistory: (params) => axios.get(API_ENDPOINTS.WEATHER_HISTORY, { params }),
  getDiagnostics: () => axios.get(API_ENDPOINTS.WEATHER_DIAGNOSTICS),
};

// ─── Auth ────────────────────────────────────────────────────────────
export const authApi = {
  login: (data) => axios.post(API_ENDPOINTS.AUTH_LOGIN, data),
  logout: () => axios.post(API_ENDPOINTS.AUTH_LOGOUT),
  getMe: () => axios.get(API_ENDPOINTS.AUTH_ME),
  getUsers: () => axios.get(API_ENDPOINTS.AUTH_USERS),
  updateUser: (id, data) => axios.patch(API_ENDPOINTS.AUTH_USER_DETAIL(id), data),
  createUser: (data) => axios.post(API_ENDPOINTS.AUTH_USERS, data),
};

// ─── Analytics ───────────────────────────────────────────────────────
export const analyticsApi = {
  getSummary: (params) => axios.get(API_ENDPOINTS.ANALYTICS_SUMMARY, { params }),
};

// ─── NDVI ────────────────────────────────────────────────────────────
export const ndviApi = {
  getProxy: (params) => axios.get(API_ENDPOINTS.NDVI_PROXY, { params }),
  getFieldSummary: (fieldId) =>
    axios.get(API_ENDPOINTS.NDVI_FIELD_SUMMARY(fieldId), { timeout: 60000 }),
  getZoneStats: (fieldId, zoneId, params) =>
    axios.get(API_ENDPOINTS.NDVI_ZONE_STATS(fieldId, zoneId), { params }),
  getAnalytics: (params) => axios.get(API_ENDPOINTS.NDVI_ANALYTICS, { params }),
};

// ─── Tour ─────────────────────────────────────────────────────────────
export const tourApi = {
  getStatus: () => axios.get(API_ENDPOINTS.TOUR_STATUS),
  seed: () => axios.post(API_ENDPOINTS.TOUR_SEED),
  exit: () => axios.post(API_ENDPOINTS.TOUR_EXIT),
  reset: () => axios.post(API_ENDPOINTS.TOUR_RESET),
};
